/**
 * AsProjectItem Component
 *
 * A clickable list item showing a single project with:
 * - Project name (or "Untitled")
 * - Unsaved indicator "(Unsaved)"
 * - Petrol-blue left marker when `current` attribute is present
 * - Action buttons: Save, Rename, Delete
 *
 * ## Attributes
 * - `project-id`: Unique identifier for the project
 * - `project-name`: Display name for the project
 * - `current`: When present, shows petrol-blue bar indicating active project
 * - `unsaved`: When present, shows unsaved indicator
 *
 * ## Events Dispatched
 * - `as-project-select`: Bubbles, composed - `{ projectId: string }` (also fired by restore button)
 * - `as-project-save`: Bubbles, composed - `{ projectId: string }`
 * - `as-project-rename`: Bubbles, composed - `{ projectId: string }`
 * - `as-project-delete`: Bubbles, composed - `{ projectId: string }`
 *
 * ## Usage
 * ```html
 * <as-project-item
 *   project-id="proj-123"
 *   project-name="My Project"
 *   current
 *   unsaved
 * ></as-project-item>
 * ```
 */
import '../base/buttons/as-icon-button.ts';
import '../overlays/as-tooltip-wrapper.ts';
import styleText from './as-project-item.css?raw';

export class AsProjectItem extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private isEditing = false;
  private errorTimeout: number | null = null;

  static get observedAttributes(): string[] {
    return ['project-id', 'project-name', 'current', 'unsaved', 'undownloaded', 'editing'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsProjectItem.sheet];
    this.render();
  }

  connectedCallback(): void {
    // Listen for save error events from as-app
    window.addEventListener('as-project-save-error', this.handleSaveError as EventListener);
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-project-save-error', this.handleSaveError as EventListener);
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  private handleSaveError = (event: CustomEvent): void => {
    const { projectId } = event.detail;
    if (projectId === this.getAttribute('project-id')) {
      this.showSaveError();
    }
  };

  private render(): void {
    const projectName = this.getAttribute('project-name') || 'Untitled';
    const isCurrent = this.hasAttribute('current');
    const currentLabel = isCurrent ? '<span class="current-label">(Current)</span>' : '';

    const projectItemContent = /*html*/`
      <div class="project-item${isCurrent ? '' : ' openable'}" tabindex="0">
        <div class="project-header">
          <div class="project-title-row">
            <span class="project-name">${projectName}</span>
            <input type="text" class="rename-input" value="${projectName}" style="display: none;" />
            <as-icon-button
              class="rename-btn"
              name="project-rename"
              file-name="icons/pencil.svg"
              tooltip-default-text="Rename"
              tooltip-placement="bottom"
              black
            ></as-icon-button>
            ${currentLabel}
          </div>
          <div class="project-actions">
            <as-icon-button
              name="project-restore"
              file-name="header/restore.png"
              tooltip-default-text="Restore saved state"
              tooltip-placement="bottom"
              black
            ></as-icon-button>
            <as-icon-button
              name="project-save"
              file-name="icons/save.svg"
              tooltip-default-text="Save"
              tooltip-placement="bottom"
              black
            ></as-icon-button>
            <as-icon-button
              name="project-delete"
              file-name="icons/delete.svg"
              tooltip-default-text="Delete"
              tooltip-placement="bottom"
              black
            ></as-icon-button>
          </div>
        </div>
        <div class="project-status"></div>
        <div class="save-error">Please name your project before saving</div>
      </div>
    `;

    // Wrap non-current projects in tooltip for "Open" hint
    if (isCurrent) {
      this.shadowRoot!.innerHTML = projectItemContent;
    } else {
      this.shadowRoot!.innerHTML = /*html*/`
        <as-tooltip-wrapper default-text="Open" placement="bottom" show-on-hover block>
          ${projectItemContent}
        </as-tooltip-wrapper>
      `;
    }

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    const item = this.shadowRoot?.querySelector('.project-item');
    const renameInput = this.shadowRoot?.querySelector('.rename-input') as HTMLInputElement;
    const isOpenable = item?.classList.contains('openable');

    // For non-current (openable) projects, clicking anywhere on the item opens it
    if (isOpenable) {
      item?.addEventListener('click', (e) => {
        // Don't trigger if clicking on buttons or inputs
        const target = e.target as HTMLElement;
        if (target.closest('as-icon-button') || target.closest('as-tooltip-wrapper:not(:first-child)') || target.tagName === 'INPUT') {
          return;
        }
        if (!this.isEditing) {
          this.handleSelect();
        }
      });
    }

    item?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (!this.isEditing && isOpenable) {
          e.preventDefault();
          this.handleSelect();
        }
      }
    });

    // Rename input event listeners
    renameInput?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.confirmRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.cancelRename();
      }
    });

    renameInput?.addEventListener('blur', () => {
      // Small delay to allow click events to fire first
      setTimeout(() => {
        if (this.isEditing) {
          this.confirmRename();
        }
      }, 100);
    });

    // Handle button clicks
    this.shadowRoot?.addEventListener('as-button-click', (e: CustomEvent) => {
      e.stopPropagation();
      const buttonName = e.detail?.buttonName;
      const projectId = this.getAttribute('project-id');

      if (!projectId) return;

      switch (buttonName) {
        case 'project-restore':
          this.dispatchEvent(new CustomEvent('as-project-select', {
            detail: { projectId },
            bubbles: true,
            composed: true
          }));
          break;
        case 'project-save':
          this.handleSave(projectId);
          break;
        case 'project-rename':
          this.startRename();
          break;
        case 'project-delete':
          this.dispatchEvent(new CustomEvent('as-project-delete', {
            detail: { projectId },
            bubbles: true,
            composed: true
          }));
          break;
      }
    });
  }

  /**
   * Start inline rename mode
   */
  private startRename(): void {
    const nameEl = this.shadowRoot?.querySelector('.project-name') as HTMLElement;
    const renameInput = this.shadowRoot?.querySelector('.rename-input') as HTMLInputElement;

    if (!nameEl || !renameInput) return;

    this.isEditing = true;
    this.setAttribute('editing', '');

    // Set input value and show it
    renameInput.value = this.getAttribute('project-name') || 'Untitled';
    nameEl.style.display = 'none';
    renameInput.style.display = 'block';

    // Focus and select text
    renameInput.focus();
    renameInput.select();
  }

  /**
   * Confirm the rename and dispatch event
   */
  private confirmRename(): void {
    const nameEl = this.shadowRoot?.querySelector('.project-name') as HTMLElement;
    const renameInput = this.shadowRoot?.querySelector('.rename-input') as HTMLInputElement;
    const projectId = this.getAttribute('project-id');

    if (!nameEl || !renameInput || !projectId) return;

    const newName = renameInput.value.trim() || 'Untitled';

    // Update the display
    nameEl.textContent = newName;
    this.setAttribute('project-name', newName);

    // Hide input, show name
    renameInput.style.display = 'none';
    nameEl.style.display = '';

    this.isEditing = false;
    this.removeAttribute('editing');

    // Dispatch rename event with new name
    this.dispatchEvent(new CustomEvent('as-project-rename', {
      detail: { projectId, newName },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Cancel rename and restore original name
   */
  private cancelRename(): void {
    const nameEl = this.shadowRoot?.querySelector('.project-name') as HTMLElement;
    const renameInput = this.shadowRoot?.querySelector('.rename-input') as HTMLInputElement;

    if (!nameEl || !renameInput) return;

    // Hide input, show name
    renameInput.style.display = 'none';
    nameEl.style.display = '';

    this.isEditing = false;
    this.removeAttribute('editing');
  }

  /**
   * Handle save with validation for untitled projects
   */
  private handleSave(projectId: string): void {
    const projectName = this.getAttribute('project-name') || 'Untitled';

    // Check if project is untitled - show error locally
    if (projectName === 'Untitled') {
      this.showSaveError();
      return;
    }

    // Hide any existing error
    this.hideSaveError();

    // Dispatch save event with name for validation
    this.dispatchEvent(new CustomEvent('as-project-save', {
      detail: { projectId, projectName },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Show the save error message
   */
  private showSaveError(): void {
    const errorEl = this.shadowRoot?.querySelector('.save-error');
    if (errorEl) {
      errorEl.classList.add('show');

      // Clear any existing timeout
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }

      // Auto-hide after 5 seconds
      this.errorTimeout = window.setTimeout(() => {
        this.hideSaveError();
      }, 5000);
    }
  }

  /**
   * Hide the save error message
   */
  private hideSaveError(): void {
    const errorEl = this.shadowRoot?.querySelector('.save-error');
    if (errorEl) {
      errorEl.classList.remove('show');
    }
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }
  }

  private handleSelect(): void {
    const projectId = this.getAttribute('project-id');
    if (projectId) {
      this.dispatchEvent(new CustomEvent('as-project-select', {
        detail: { projectId },
        bubbles: true,
        composed: true
      }));
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'project-name':
        const nameEl = this.shadowRoot?.querySelector('.project-name');
        if (nameEl) {
          nameEl.textContent = newValue || 'Untitled';
        }
        break;
      case 'current':
        // Re-render to update the (Current) label
        this.render();
        break;
    }
  }

  get projectId(): string | null {
    return this.getAttribute('project-id');
  }

  set projectId(value: string | null) {
    if (value) {
      this.setAttribute('project-id', value);
    } else {
      this.removeAttribute('project-id');
    }
  }

  get projectName(): string {
    return this.getAttribute('project-name') || 'Untitled';
  }

  set projectName(value: string) {
    this.setAttribute('project-name', value);
  }

  get current(): boolean {
    return this.hasAttribute('current');
  }

  set current(value: boolean) {
    if (value) {
      this.setAttribute('current', '');
    } else {
      this.removeAttribute('current');
    }
  }

  get unsaved(): boolean {
    return this.hasAttribute('unsaved');
  }

  set unsaved(value: boolean) {
    if (value) {
      this.setAttribute('unsaved', '');
    } else {
      this.removeAttribute('unsaved');
    }
  }

  get undownloaded(): boolean {
    return this.hasAttribute('undownloaded');
  }

  set undownloaded(value: boolean) {
    if (value) {
      this.setAttribute('undownloaded', '');
    } else {
      this.removeAttribute('undownloaded');
    }
  }
}

if (!customElements.get('as-project-item')) {
  customElements.define('as-project-item', AsProjectItem);
} else {
  console.warn('as-project-item already defined');
}
